import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { fetchSupportData, updateSupportStatus } from "../../api/support";
import { setSearchTermAction } from "../app-container/reducer/filterSlice";
import { formatDate } from "../../utils/dateTime";
import { useNotification } from "../../hooks/useNotification";
import UserTableSkeleton from "../../components/user-management/UserTableSkeleton";
import TicketConversation from "./TicketConversation";
import { Pagination } from "@mui/material";
import { initColumns, toggleColumn } from "../app-container/reducer/visibleColumnsSlice";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '1399px',
  width: '95%',
  margin: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24, borderRadius: '20px',
  maxHeight: '90vh',
  overflow: 'auto',
  padding: '0 30px 30px 30px',
};
const visibleColumnKey =[
  "email",
  "isRegistered",
  "fullName",
  "concern",
  "description",
  "createdAt",
  "status",
  "action"
]


function QueryTable({
  searchTerm: parentSearchTerm = '',
  setSearchTerm: setParentSearchTerm,
  currentPage,
  setCurrentPage,
  pageLimit,
  setPageLimit,
  filter,
  loading: parentLoading,
  fetchParentData, // optional trigger
}) {
  const dispatch = useDispatch();
  const { showErrorNotification, showSuccessNotification } = useNotification();

  // refs for debounce + abort + scrolling
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);

  // local UI state
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [open, setOpen] = useState(false);
  const [openDescriptionModal, setOpenDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0, scrollWidth: 0, clientWidth: 0,
  });
  const visibleColumns = useSelector(state => state.visibleColumns.pages.QueryTicketManagement || {});

  // Keep a local searchTerm to control input immediately
  const [searchTerm, setSearchTermLocal] = useState(parentSearchTerm ?? '');

  // When parent updates the searchTerm (from store or other), sync local input
  useEffect(() => {
    if ((parentSearchTerm ?? '') !== (searchTerm ?? '')) {
      setSearchTermLocal(parentSearchTerm ?? '');
      setCurrentPage && setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSearchTerm]);

  // Scroll helpers
  const updateScrollState = useCallback(() => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setScrollState({ scrollLeft, scrollWidth, clientWidth });
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState, userData, visibleColumns]);

  // Derived filtered data based on local searchTerm
  const filteredData = useMemo(() => {
    const term = (searchTerm ?? '').toLowerCase().trim();
    if (!Array.isArray(userData)) return [];
    return userData.filter((item = {}) => {
      const matchesFilter =
        activeFilter === "All" ||
        (activeFilter === "Registered Users" && item.isRegister === true) ||
        (activeFilter === "Non Registered Users" && item.isRegister === false);

      if (!term) return matchesFilter;

      const matchesSearch =
        item.fullName?.toLowerCase().includes(term) ||
        item.email?.toLowerCase().includes(term) ||
        item.concern?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        (item.createdAt ? formatDate(item.createdAt).toLowerCase().includes(term) : false);

      return matchesFilter && matchesSearch;
    });
  }, [userData, searchTerm, activeFilter]);

  // fetchSupportData wrapper
  const fetchData = useCallback(async (signal) => {
    try {
      setLoading(true);
      const data = await fetchSupportData(setUserData, currentPage || 1, pageLimit || 25, 0, filter, signal);
      setTotalUsers(data?.total ?? data?.totalUsers ?? 0);
      setTotalPages(data?.totalPages ?? 0);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error("fetchSupportData error", err);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageLimit, filter]);

  // Debounced fetch when searchTerm changes or pagination changes
  useEffect(() => {
    // Normalize searchTerm so null/undefined becomes ''
    const term = parentSearchTerm ?? searchTerm ?? '';
    // Clear previous debounce
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    // Abort previous fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    debounceTimeoutRef.current = setTimeout(() => {
      fetchData(controller.signal);
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [parentSearchTerm, currentPage, pageLimit, filter, fetchData]);

  // When the user types in the search input:
  const handleSearchChange = (e) => {
    const value = e.target.value ?? '';
    setSearchTermLocal(value);
    // update parent (which may update store) and also dispatch to store
    setParentSearchTerm && setParentSearchTerm(value);
    dispatch(setSearchTermAction({ searchTerm: value }));
    // reset page
    setCurrentPage && setCurrentPage(1);
  };

  const handleStatus = async (supportId, status) => {
    try {
      setLoading(true);
      const data = await updateSupportStatus({ supportId, status });
      if (data) {
        showSuccessNotification('Status successfully updated');
        // refresh local data
        fetchData();
        fetchParentData();
      }
    } catch (error) {
      console.error('Error while updating status:', error);
      showErrorNotification('Error updating status');
    } finally {
      setLoading(false);
    }
  };

  // UI: basic column headers
  const columnHeaders = [
    { key: "email", label: "Email" },
    { key: "fullName", label: "Full Name" },
    { key: "concern", label: "Concern" },
    { key: "description", label: "Description" },
    { key: "createdAt", label: "Created At" },
    { key: "status", label: "Status" },
    { key: "action", label: "Action" },
  ];

  const handleViewDescription = (description) => {
    setSelectedDescription(description);
    setOpenDescriptionModal(true);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };
  const handleColumnToggle = (key) => {
    dispatch(
    toggleColumn({
      pageKey: "QueryTicketManagement",
      column: key
    }) );
  };

    useEffect(() => {
    dispatch(
      initColumns({
        pageKey: "QueryTicketManagement",
        columns: visibleColumnKey
      })
    );
  }, []);

  return (
    <>
      <div className="mb-0 d-flex justify-content-between align-items-center">
        <h6 className="text-lg mb-0">{filter?.statsTitle ?? "Query Ticket List"}</h6>
        <div className="d-flex align-items-center gap-2">
          <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              name="search"
              placeholder="Search"
              value={searchTerm}
              autoComplete="off"
              onChange={handleSearchChange}
            />
            <span className="icon">
              <iconify-icon icon="ion:search-outline" />
            </span>
          </form>

          <div className="dropdown">
            <button
              className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <ViewColumnOutlinedIcon />
            </button>
            <ul className="dropdown-menu manage-list">
              {columnHeaders.map((column) => (
                <li key={column.key} className="dropdown-item d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    style={{ width: '18px', height: '18px' }}
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => handleColumnToggle(column.key)}
                  />
                  <label className="ms-2">{column.label}</label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="d-flex align-items-center gap-3">
        {["All", "Registered Users", "Non Registered Users"].map((f) => (
          <button
            key={f}
            className={`badge text-sm fw-medium px-20 py-12 radius-6 ${activeFilter === f ? "text-warning-900 bg-warning-50" : "bg-light-100 text-secondary-light"}`}
            onClick={() => setActiveFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="table-scroll-container mt-3">
        <div
          ref={tableContainerRef}
          className="custom-horizontal-scroll scroll-sm"
          onScroll={() => {
            if (tableContainerRef.current) {
              const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
              setScrollState({ scrollLeft, scrollWidth, clientWidth });
            }
          }}
           style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
        >
          <table className="table bordered-table sm-table mb-0" style={{position: 'relative',}}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
              <tr style={{ background: "#f4f4f4", textAlign: "left" }}>
                <th>#</th>
                {visibleColumns.email && <th>Email</th>}
                {visibleColumns.isRegistered && <th>Registered User</th>}
                {visibleColumns.fullName && <th>Full Name</th>}
                {visibleColumns.concern && <th className="text-center">Concern</th>}
                {visibleColumns.description && <th className="text-center">Description</th>}
                {visibleColumns.createdAt && <th className="text-center">Created At</th>}
                {visibleColumns.status && <th className="text-center">Status</th>}
                {visibleColumns.action && <th className="text-center">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading || parentLoading ? (
                <UserTableSkeleton rows={10} columns={Object.keys(visibleColumns).filter(k => visibleColumns[k]).length + 1} />
              ) : filteredData.length > 0 ? (
                filteredData.map((item, index) => {
                  const isTruncated =
                    item.description && item.description.length > 60;
                  return (
                    <tr key={item._id || index}>
                      <td>{index + 1}</td>
                      {visibleColumns.email && <td>{item.email}</td>}
                      {visibleColumns.isRegistered && <td>{item?.isRegister ? "Yes" : "No"}</td>}
                      {visibleColumns.fullName && <td>{item.fullName}</td>}
                      {visibleColumns.concern && <td className="text-center">{item.concern}</td>}
                      {/* {visibleColumns.description && <td className="text-center">{item.description}</td>} */}
                      {visibleColumns.description && (
                        <td
                          className="text-center"
                          style={{ maxWidth: "250px" }}
                        >
                          <div
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                            }}
                          >
                            {item.description}
                          </div>
                          {isTruncated && (
                            <button
                              className="badge text-xs fw-medium border bg-transparent px-10 py-6 mt-1 radius-6 text-primary"
                              onClick={() =>
                                handleViewDescription(item.description)
                              }
                            >
                              View
                            </button>
                          )}
                        </td>
                      )}
                      {visibleColumns.createdAt && <td className="text-center">{item.createdAt ? formatDate(item.createdAt) : ''}</td>}
                      {visibleColumns.status && <td className="text-center">
                        <span className="badge text-sm fw-medium rounded-pill px-20 py-9 radius-4" style={{
                          textTransform: 'capitalize',
                          backgroundColor:
                            item.status === "open" ? "#28a745" :
                              item.status === "in-progress" ? "#fff3cd" :
                                item.status === "resolved" ? "#cce5ff" : "#f8d7da",
                          color:
                            item.status === "open" ? "#fff" :
                              item.status === "in-progress" ? "#856404" :
                                item.status === "resolved" ? "#004085" : "#721c24",
                        }}>{item.status}</span>
                      </td>}
                      {visibleColumns.action && <td className="text-center">
                        <div className="dropdown">
                          <button className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon text-sm" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            Action
                          </button>
                          <ul className="dropdown-menu">
                            <li><button className="dropdown-item" onClick={() => handleStatus(item._id, 'closed')}>Close</button></li>
                            <li><button className="dropdown-item" onClick={() => handleStatus(item._id, 'resolved')}>Resolved</button></li>
                          </ul>
                        </div>
                      </td>}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="20" className="text-center py-3">No data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Sticky Scroll Bar */}
        {scrollState.scrollWidth > scrollState.clientWidth && (
          <div className="sticky-scroll-container">
            <div className="sticky-scroll-track" onClick={(e) => {
              const track = e.currentTarget;
              const trackRect = track.getBoundingClientRect();
              const clickX = e.clientX - trackRect.left;
              const thumbWidth = Math.max((scrollState.clientWidth / scrollState.scrollWidth) * scrollState.clientWidth, 50);
              const thumbCenter = thumbWidth / 2;
              const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) * (scrollState.scrollWidth - scrollState.clientWidth);
              if (tableContainerRef.current) tableContainerRef.current.scrollLeft = newScrollLeft;
            }}>
              <div ref={scrollThumbRef} className="sticky-scroll-thumb" style={{
                width: `${Math.max((scrollState.clientWidth / Math.max(scrollState.scrollWidth, 1)) * scrollState.clientWidth, 50)}px`,
                transform: `translateX(${(scrollState.scrollLeft / Math.max(scrollState.scrollWidth - scrollState.clientWidth, 1)) * (scrollState.clientWidth - Math.max((scrollState.clientWidth / Math.max(scrollState.scrollWidth, 1)) * scrollState.clientWidth, 50))}px)`
              }} />
            </div>
          </div>
        )}

      </div>
            {!loading && Boolean(totalPages) &&  <div className="d-flex justify-content-center mt-16">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                   sx={{
    "& .MuiPaginationItem-root": {
      color: "#000",  
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important",  
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }} 
                />
              </div>}
      <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={style}>
          <TicketConversation />
        </Box>
      </Modal>

      <Modal open={openDescriptionModal} onClose={() => setOpenDescriptionModal(false)}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "500px", bgcolor: "background.paper", borderRadius: "12px", boxShadow: 24, p: 4, maxHeight: "80vh", overflowY: "auto",
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Full Description</Typography>
          <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "text.secondary" }}>{selectedDescription}</Typography>
          <Box textAlign="right" mt={3}>
            <Button variant="contained" color="primary" onClick={() => setOpenDescriptionModal(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
export default QueryTable;
